import React, { useEffect, useState } from 'react';
import {
  Platform,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

import TimeOutErrorModal from '../../Authenticated/Usage/TimeOutErrorModal';

import {
  useConsumerStatusSummaryQuery,
  useConsumerUsageSummaryQuery,
  useMultiPayContractsQuery,
} from 'apollo/queries/autogenerate/hooks';
import { paymentDataFragment } from 'apollo/queries/autogenerate/operations';
import {
  Charge,
  MultipayContractStatus,
} from 'apollo/queries/autogenerate/schemas';
import { isPlusUser } from 'shared/consumer/utils/consumer-types';
import Dialog from 'shared/modules/common/components/atoms/Dialog';
import Icon from 'shared/modules/common/components/atoms/Icon';
import Loading from 'shared/modules/common/components/atoms/Loading';
import useHandleViewCharge from 'shared/modules/common/hooks/use-handle-view-charge';
import generateID from 'shared/modules/common/utils/generate-id';
import InfoContent from 'shared/modules/usage/components/molecules/InfoContent';
import MonthlyChargesTabs from 'shared/modules/usage/components/organisms/MonthlyChargesTabs';
import PlusChargesSection from 'shared/modules/usage/components/organisms/PlusChargesSection';
import RecentDebits from 'shared/modules/usage/components/organisms/RecentDebits';
import RecentSettlements from 'shared/modules/usage/components/organisms/RecentSettlements';
import SummaryCard from 'shared/modules/usage/components/organisms/SummaryCard/SummaryCard';
import UsageScrollOnboardingModal from 'shared/modules/usage/components/organisms/SummaryCard/UsageScrollOnboardingModal';
import UncapturedPaymentsSection from 'shared/modules/usage/components/organisms/UncapturedPaymentsSection';
import { ACTION, ITEM, SCREEN } from 'shared/modules/usage/constants/mixpanel';
import { createCookie, getCookie } from 'shared/utils/cookie';
import { isDateWithinRange } from 'shared/utils/date';
import testID from 'shared/utils/test-id';
import Mixpanel from 'shared/vendor/Mixpanel';
import { getCurrentInstallmentIndex } from 'utils/charge-utils';
import useIsPlusWithApple from 'utils/hooks/use-is-plus-with-apple';
import AppleChargeDetailsDialog, {
  useAppleChargeDetailsDialog,
} from 'web/modules/common/components/organisms/AppleChargeDetailsDialog';
import Footer from 'web/modules/common/components/organisms/Footer';
import InstallmentPlanDialog, {
  useInstallmentPlanDialog,
} from 'web/modules/common/components/organisms/InstallmentPlanDialog';
import ReceiptDialog, {
  useReceiptDialog,
} from 'web/modules/common/components/organisms/ReceiptDialog';
import SettlementDialog, {
  useSettlementDialog,
} from 'web/modules/usage/components/organisms/SettlementDialog';
import routes from 'web/navigation/routes';
const EVENT_PAGE_VIEW = `${SCREEN.USAGE_SUMMARY} - ${ACTION.PAGE_VIEW}`;
const EVENT_CLICK = `${SCREEN.USAGE_SUMMARY} - ${ACTION.CLICK}`;

const IS_USAGE_SCROLL_ONBOARDED = 'isUsageScrollOnboarded';

const UsageScreen = () => {
  const navigate = useNavigate();
  const isBigScreen = useMediaQuery({ minWidth: 768 });
  const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false);
  const { isPlusWithApple } = useIsPlusWithApple();
  const receiptDialog = useReceiptDialog();
  const installmentPlanDialog = useInstallmentPlanDialog();
  const appleChargeDetailsDialog = useAppleChargeDetailsDialog();
  const settlementDialog = useSettlementDialog();
  const [showTimeoutDialog, setShowTimeoutDialog] = useState(false);
  const [isRefreshLoading, setIsRefreshLoading] = useState(false);
  const [isUsageScrollOnboarded, setIsUsageScrollOnboarded] = useState(true);
  const { data: consumerStatusSummary } = useConsumerStatusSummaryQuery();
  const houkatsuStatus = consumerStatusSummary?.houkatsuStatus?.status;
  const kycStatus = consumerStatusSummary?.KYCApplicationStatus?.status;
  const isPlus = isPlusUser(kycStatus, houkatsuStatus);

  const handleViewCharge = useHandleViewCharge({
    handleAppleCharge: (charge) => {
      Mixpanel.trackEvent(EVENT_CLICK, {
        Item: ITEM.CHARGE,
      });

      appleChargeDetailsDialog.open(charge);
    },
    handleNPayCharge: (charge) => {
      Mixpanel.trackEvent(EVENT_CLICK, {
        Item: ITEM.CHARGE,
      });

      const currentIndex = getCurrentInstallmentIndex(charge);

      installmentPlanDialog.open(charge.metadata.source, currentIndex);
    },
    handleStandardCharge: (charge) => {
      Mixpanel.trackEvent(EVENT_CLICK, {
        Item: ITEM.CHARGE,
      });

      receiptDialog.open(charge);
    },
  });

  useEffect(() => {
    Mixpanel.timeEvent(EVENT_PAGE_VIEW);

    return () => {
      Mixpanel.trackEvent(EVENT_PAGE_VIEW);
    };
  }, []);

  useEffect(() => {
    if (getCookie(IS_USAGE_SCROLL_ONBOARDED)) {
      setIsUsageScrollOnboarded(true);
    } else {
      setIsUsageScrollOnboarded(false);
    }
  }, []);

  const handleRecentSettlementPress = (settlement: Charge) => {
    let settlementType = '';

    if (settlement.isPayNowSettlement) {
      settlementType = ITEM.PAY_NOW;
    } else if (settlement.isVanTransferSettlement) {
      settlementType = ITEM.BANK_WIRE;
    } else if (settlement.paidBillingCycle) {
      settlementType = ITEM.BILL;
    }

    Mixpanel.trackEvent(EVENT_CLICK, {
      Type: settlementType,
    });

    settlementDialog.open(settlement);
  };

  const handlePlusChargesBottomLinkPress = () => {
    Mixpanel.trackEvent(EVENT_CLICK, {
      Item: ITEM.CHOATOBARAI_CONTRACTS,
    });

    navigate(routes.authenticated.usage.plusContracts);
  };

  const handleViewMultipayContractsPress = () => {
    Mixpanel.trackEvent(EVENT_CLICK, {
      Item: ITEM.MULTIPAY_CONTRACTS,
    });

    navigate(routes.authenticated.usage.multiPayContracts);
  };

  const handleUncapturedPaymentItemPress = (payment: paymentDataFragment) => {
    Mixpanel.trackEvent(EVENT_CLICK, {
      Item: ITEM.UNCAPTURED,
    });

    receiptDialog.openWithPayment(payment);
  };

  const handleRecentDebitItemPress = (charge: Charge) => {
    receiptDialog.open(charge);
  };

  const {
    data: consumerUsageSummary,
    loading: isLoading,
    refetch: refetchUsageSummary,
  } = useConsumerUsageSummaryQuery({
    fetchPolicy: 'cache-and-network',
  });

  // Check if the consumer has either active, approved, pending MultipayContracts
  const { data: contracts } = useMultiPayContractsQuery({
    variables: {
      status: [
        MultipayContractStatus.ACTIVE,
        MultipayContractStatus.APPROVED,
        MultipayContractStatus.PENDING,
      ],
    },
  });

  const hasMultiPay = contracts?.multipayContracts.length !== 0;

  useEffect(() => {
    if (!isLoading && !consumerUsageSummary) {
      setShowTimeoutDialog(true);
    }
  }, [consumerUsageSummary, isLoading]);

  if (isLoading && !consumerUsageSummary) {
    return <Loading id="loading-indicator" />;
  }

  const hasPlusCharges =
    consumerUsageSummary?.consumerUsageSummary.plusCharges &&
    consumerUsageSummary?.consumerUsageSummary.plusCharges.length > 0;

  const recent90DaysDebits =
    consumerUsageSummary &&
    (consumerUsageSummary.consumerUsageSummary
      .recentDebits as Charge[]).filter((d) =>
      isDateWithinRange(new Date(d.pushedAt), 90)
    );

  if (isRefreshLoading) {
    return <Loading id="loading-indicator" />;
  }

  const startTimer = async () => {
    setIsRefreshLoading(true);
    setTimeout(() => {
      setIsRefreshLoading(false);
      refetchUsageSummary();
    }, 5000);
  };

  return (
    <View style={[styles.screen, isBigScreen && styles.screenBigScreen]}>
      <TimeOutErrorModal
        open={showTimeoutDialog}
        onCloseButtonPress={() => {
          setShowTimeoutDialog(false);
        }}
        onTryButtonPress={() => {
          setShowTimeoutDialog(false);
          startTimer();
        }}
      />
      <View
        style={[styles.container, isBigScreen && styles.containerBigScreen]}
      >
        <View
          style={[
            styles.headerContainer,
            isBigScreen && styles.headerContainerBigScreen,
          ]}
        >
          <View
            style={[styles.header, isBigScreen && { paddingHorizontal: 0 }]}
          >
            <View style={styles.headerBar}>
              <Text style={styles.headerTitle}>ご利用明細</Text>
              <TouchableOpacity
                {...testID('help_button')}
                onPress={() => setIsInfoDialogOpen(true)}
                /** Generate HTML and Test IDs  */
                {...generateID('usage-screen-help-button')}
                /** Platform-specific props */
                {...Platform.select({
                  web: {
                    accessibilityRole: 'button',
                  },
                })}
              >
                <Icon name="help" size="24" tintColor={'#2a2e32'} />
              </TouchableOpacity>
            </View>
            <SummaryCard isPlusWithApple={isPlusWithApple} />
          </View>
          {(isPlus || hasMultiPay) && (
            <View
              style={[
                styles.mainContent,
                styles.amountColumn,
                isBigScreen && { marginHorizontal: 0 },
              ]}
            >
              <TouchableOpacity
                onPress={() => {
                  navigate(routes.authenticated.usage.contractsSummary);
                }}
              >
                <View style={styles.summaryLinkContainer}>
                  <View style={[styles.group, styles.leftPart]}>
                    <Icon name="npay" size="24" style={styles.icon} />
                    <Text style={styles.text}>分割あと払いの詳細</Text>
                  </View>
                  <View style={[styles.group, styles.rightPart]}>
                    <Text style={styles.linkText}>すべて見る</Text>
                    <Icon name="caret-right" size="24" />
                  </View>
                </View>
              </TouchableOpacity>
            </View>
          )}
        </View>
        <View style={styles.chargesSection}>
          <UncapturedPaymentsSection
            onPaymentItemPress={handleUncapturedPaymentItemPress}
          />
          <MonthlyChargesTabs
            onChargeItemPress={handleViewCharge}
            onViewMultipayContractsPress={handleViewMultipayContractsPress}
          />
          <RecentSettlements onItemPress={handleRecentSettlementPress} />
          <RecentDebits
            onItemPress={handleRecentDebitItemPress}
            data={recent90DaysDebits}
          />
          {hasPlusCharges && (
            <PlusChargesSection
              onChargeItemPress={handleViewCharge}
              onBottomLinkPress={handlePlusChargesBottomLinkPress}
            />
          )}
        </View>
        <Footer />
      </View>
      <Dialog
        title=""
        onCloseButtonPress={() => setIsInfoDialogOpen(false)}
        open={isInfoDialogOpen}
        id="usage-screen-info-dialog"
      >
        <InfoContent
          isPayNowEnabled={false}
          isPlusWithApple={isPlusWithApple}
        />
      </Dialog>
      <ReceiptDialog
        charge={receiptDialog.charge}
        payment={receiptDialog.payment}
        open={receiptDialog.isOpen}
        onCloseButtonPress={receiptDialog.close}
      />
      <SettlementDialog
        charge={settlementDialog.charge}
        open={settlementDialog.isOpen}
        onCloseButtonPress={settlementDialog.close}
        onChargeItemPress={handleViewCharge}
        onBillItemPress={(bill) => {
          navigate(
            routes.authenticated.home.billDetails.replace(
              ':id',
              bill.billStatementId
            )
          );
        }}
      />
      {installmentPlanDialog.installmentPlanId && (
        <InstallmentPlanDialog
          installmentPlanId={installmentPlanDialog.installmentPlanId}
          currentInstallmentIndex={installmentPlanDialog.currInstallmentIndex}
          open={installmentPlanDialog.isOpen}
          onCloseButtonPress={installmentPlanDialog.close}
        />
      )}
      {appleChargeDetailsDialog.charge && (
        <AppleChargeDetailsDialog
          charge={appleChargeDetailsDialog.charge}
          open={appleChargeDetailsDialog.isOpen}
          onCloseButtonPress={appleChargeDetailsDialog.close}
        />
      )}
      <UsageScrollOnboardingModal
        visible={!isUsageScrollOnboarded}
        onClose={() => {
          createCookie(IS_USAGE_SCROLL_ONBOARDED, 'true');
          setIsUsageScrollOnboarded(true);
        }}
      />
    </View>
  );
};

export default UsageScreen;

const styles = StyleSheet.create({
  // Template for building a new main screen
  // Scrolls on overflow for both mobile and desktop
  screen: {
    alignItems: 'center',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    overflow: 'scroll',
    paddingBottom: 24,
    paddingHorizontal: 16,
  },
  screenBigScreen: {
    paddingHorizontal: 24,
  },
  container: {
    width: '100%',
  },
  containerBigScreen: {
    maxWidth: 560,
  },
  headerContainer: {
    marginHorizontal: -16,
  },
  headerContainerBigScreen: {
    marginHorizontal: 0,
  },
  header: {
    padding: 16,
  },
  headerBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    lineHeight: 30,
    color: '#2a2e32',
  },
  chargesSection: {
    flex: 1,
    paddingBottom: 24,
  },
  summaryLinkContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginHorizontal: 16,
  },
  group: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  mainContent: {
    borderRadius: 24,
    backgroundColor: '#fff',
    marginHorizontal: 16,
    marginTop: 16,
  },
  amountColumn: {
    flex: 1,
    borderRadius: 12,
  },
  linkText: {
    fontSize: 12,
    fontWeight: '600',
    lineHeight: 16,
    color: '#e5258c',
    paddingVertical: 16,
    marginLeft: 8,
  },
  text: {
    fontSize: 12,
    fontWeight: '600',
    lineHeight: 16,
    color: '#2a2e32',
  },
  icon: {
    marginRight: 16,
  },
  leftPart: {
    justifyContent: 'flex-start',
  },
  rightPart: {
    justifyContent: 'flex-end',
  },
});
